// src/components/dashboard/AssignmentsPanel.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip
} from '@mui/material';
import { API_BASE } from '../../utils/api';
import AssignmentDetailModal from './AssignmentDetailModal';

const TYPE_LABELS = {
  draft: 'Manuscript',
  cover_task: 'Cover Task',
  manuscript_goal: 'Goal',
  cover_version: 'Cover Version',
  cover_offer: 'Cover Offer',
};

const AssignmentsPanel = () => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [sortBy, setSortBy] = useState('due_date');
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  useEffect(() => {
    fetchAssignments();
  }, []);

  const fetchAssignments = async () => {
    try {
      const token = localStorage.getItem('rv_access_token');
      const response = await fetch(`${API_BASE}/api/invitations/incoming?status=accepted`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAssignments(data);
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (a) => {
    setSelectedAssignment(a);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isOverdue = (dateString) => {
    if (!dateString) return false;
    return new Date(dateString) < new Date();
  };

  const getTitle = (a) => a.taskName || a.draftTitle || 'Untitled';
  const getDescription = (a) => a.taskDetail || a.message || '';

  const filtered = assignments
    .filter((a) => {
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        getTitle(a).toLowerCase().includes(q) ||
        (a.draftTitle || '').toLowerCase().includes(q) ||
        getDescription(a).toLowerCase().includes(q);
      const matchesType = !filterType || a.type === filterType;
      return matchesSearch && matchesType;
    })
    .sort((a, b) => {
      if (sortBy === 'due_date') {
        return new Date(a.dueDate || 0) - new Date(b.dueDate || 0);
      }
      return getTitle(a).localeCompare(getTitle(b));
    });

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading assignments...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          ACTIVE ASSIGNMENTS
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="SEARCH BY TITLE"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>FILTER BY</InputLabel>
            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              label="FILTER BY"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="draft">Manuscript</MenuItem>
              <MenuItem value="cover_task">Cover Task</MenuItem>
              <MenuItem value="manuscript_goal">Goal</MenuItem>
              <MenuItem value="cover_version">Cover Version</MenuItem>
              <MenuItem value="cover_offer">Cover Offer</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>SORT BY</InputLabel>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              label="SORT BY"
            >
              <MenuItem value="due_date">Due Date</MenuItem>
              <MenuItem value="title">Title</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {filtered.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#f9f9f9', borderRadius: 1 }}>
          <Typography color="text.secondary">
            {assignments.length === 0 ? 'No active assignments' : 'No assignments match your filters'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filtered.map((a) => (
            <Box
              key={a.id}
              onClick={() => handleClick(a)}
              sx={{
                p: 2,
                bgcolor: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  borderColor: '#1976d2'
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {getTitle(a)}
                </Typography>
                <Chip
                  label={`DUE DATE: ${formatDate(a.dueDate)}`}
                  size="small"
                  color={isOverdue(a.dueDate) ? 'error' : 'default'}
                  variant="outlined"
                />
              </Box>

              {getDescription(a) && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {getDescription(a)}
                </Typography>
              )}

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Book:</strong> {a.draftTitle || 'Untitled'}
                </Typography>
                {a.fromUsername && (
                  <Typography variant="caption" color="text.secondary">
                    <strong>Author:</strong> {a.fromUsername}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <AssignmentDetailModal
        inv={selectedAssignment}
        open={!!selectedAssignment}
        onClose={() => setSelectedAssignment(null)}
        onUnassigned={() => {
          setSelectedAssignment(null);
          fetchAssignments();
        }}
      />
    </Box>
  );
};

export default AssignmentsPanel;
